import {useTranslations} from 'next-intl';
import React, {FC, useEffect, useState} from 'react';
import {toast} from 'react-toastify';
import logger from '../../logger';
import {updateUser} from '@/services/user';
import useSession from '../../stores/session/useSession';
import z from 'zod';
import {useFormik} from 'formik';
import {toFormikValidationSchema} from 'zod-formik-adapter';
import {Input, Button, Checkbox} from '@/components/design-system';
import {BillingData} from '@winelivery-org/wl-client-sdk-js';

interface IInvoice {
  businessName: string;
  address: string;
  vat?: string;
  socialSecurityNumber?: string;
  pec?: string;
}

interface IProps {
  invoice: IInvoice | undefined;
  onSubmitted: (invoice: IInvoice) => void;
}

const schema = z
  .object({
    businessName: z.string({required_error: 'form.errors.required'}),
    address: z.string({required_error: 'form.errors.required'}),
    vat: z.string({required_error: 'form.errors.required'}).optional(),
    socialSecurityNumber: z
      .string({required_error: 'form.errors.required'})
      .optional(),
    pec: z
      .string({required_error: 'form.errors.required'})
      .email({message: 'form.errors.email'})
      .max(100, {message: 'form.errors.max'}),
  })
  .superRefine(({socialSecurityNumber, vat}, ctx) => {
    if (!socialSecurityNumber && !vat) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['socialSecurityNumber'],
        message: 'form.errors.required',
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['vat'],
        message: 'form.errors.required',
      });
    }
  });

export const InvoiceForm: FC<IProps> = ({onSubmitted, invoice}) => {
  const t = useTranslations('common');

  const [flagged, setFlagged] = useState<boolean>(false);
  const {setUser} = useSession();

  const {
    handleChange,
    isSubmitting,
    setSubmitting,
    isValid,
    touched,
    setFieldTouched,
    values: {businessName, address, vat, socialSecurityNumber, pec},
    errors,
  } = useFormik({
    validationSchema: toFormikValidationSchema(schema),
    initialValues: {
      businessName: invoice?.businessName || '',
      address: invoice?.address || '',
      vat: invoice?.vat || '',
      socialSecurityNumber: invoice?.socialSecurityNumber || '',
      pec: invoice?.pec || '',
    },
    onSubmit: async () => {
      await onSubmit();
    },
  });

  useEffect(() => {
    const localFlagged = localStorage.getItem('use_invoice_for_next_orders');
    setFlagged(localFlagged === 'true');
  }, []);

  const onSubmit = async () => {
    const values: BillingData = {
      businessName,
      address,
      vat,
      socialSecurityNumber,
      pec,
    };

    setSubmitting(true);
    try {
      const {ok, data} = await updateUser({
        billingData: [values],
      });
      if (!ok) {
        throw new Error('Error during request');
      }
      setUser(data);
      onSubmitted(values);
      localStorage.setItem('use_invoice_for_next_orders', flagged.toString());
    } catch (err) {
      logger.error(err);
      toast.error('Error during request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="tw-py-4 tw-px-6 tw-rounded-md tw-border tw-border-greyscale-gray-light tw-bg-white tw-m-0 tw-mb-1">
        <div className="tw-grid tw-grid-cols-2 tw-gap-4">
          <div className="max-sm:tw-col-span-2">
            <Input
              label={t('InvoiceForm.invoice-intestazione')}
              disabled={isSubmitting}
              value={businessName}
              name="businessName"
              id="businessName"
              onChange={(e) => {
                handleChange(e);
                if (!touched.businessName) {
                  setFieldTouched('businessName', true, true);
                }
              }}
              {...(errors.businessName &&
                touched.businessName && {errorMessage: t(errors.businessName)})}
            />
          </div>
          <div className="max-sm:tw-col-span-2">
            <Input
              label={t('InvoiceForm.invoice-sede-legale')}
              disabled={isSubmitting}
              value={address}
              name="address"
              id="address"
              onChange={(e) => {
                handleChange(e);
                if (!touched.address) {
                  setFieldTouched('address', true, true);
                }
              }}
              {...(errors.address &&
                touched.address && {errorMessage: t(errors.address)})}
            />
          </div>
          <div className="max-sm:tw-col-span-2">
            <Input
              label={t('InvoiceForm.invoice-vat')}
              disabled={isSubmitting}
              value={vat}
              name="vat"
              id="vat"
              onChange={(e) => {
                handleChange(e);
                if (!touched.vat) {
                  setFieldTouched('vat', true, true);
                  setFieldTouched('socialSecurityNumber', true, true);
                }
              }}
              {...(errors.vat && touched.vat && {errorMessage: t(errors.vat)})}
            />
          </div>
          <div className="max-sm:tw-col-span-2">
            <Input
              label={t('InvoiceForm.invoice-ssn')}
              disabled={isSubmitting}
              value={socialSecurityNumber}
              name="socialSecurityNumber"
              id="socialSecurityNumber"
              onChange={(e) => {
                handleChange(e);
                if (!touched.socialSecurityNumber) {
                  setFieldTouched('vat', true, true);
                  setFieldTouched('socialSecurityNumber', true, true);
                }
              }}
              {...(errors.socialSecurityNumber &&
                touched.socialSecurityNumber && {
                  errorMessage: t(errors.socialSecurityNumber),
                })}
            />
          </div>
          <div className="tw-col-span-2">
            <Input
              label={t('InvoiceForm.invoice-codice-destinatario')}
              disabled={isSubmitting}
              value={pec}
              name="pec"
              id="pec"
              onChange={(e) => {
                handleChange(e);
                if (!touched.pec) {
                  setFieldTouched('pec', true, true);
                }
              }}
              {...(errors.pec && touched.pec && {errorMessage: t(errors.pec)})}
            />
          </div>
        </div>
      </div>
      <div className="tw-mt-4 tw-flex tw-items-center tw-gap-4">
        <div>
          <Checkbox
            label={t('InvoiceForm.use-for-next-orders')}
            onChange={(e) => {
              setFlagged(e.target.checked);
            }}
            checked={flagged}
            id="next-order-invoice"
          />
        </div>
        <div className="tw-flex-1">
          <Button
            className="tw-w-full"
            loading={isSubmitting}
            onClick={onSubmit}
            disabled={isSubmitting || !isValid}
          >
            {t('InvoiceForm.save')}
          </Button>
        </div>
      </div>
    </>
  );
};
